'use strict';

const _ = require('lodash');
const errors = require('../../errors');
const utils = require('../../utils/utils');
const workerUtils = require('../workerUtils');
const scriptsValidator = require('../scriptsValidator');
const platformUtilities = require('../../utils/platformUtilities');
const platformUtils = require('santa-platform-utils');
const platformServices = require('../../platformServices');
const ravenWorkerServices = require('../../ravenWorkerServices');
const biStore = require('../../stores/biStore');
const bi = require('../../bi');
const storeConstants = require('../../constants/store');
const { USE_WIX_CODE_APP_EXPERIMENT } = require('../../constants/constants');
const {setElementoryArguments} = require('../elementoryArguments');
const wixCodeViewerApp = require('./../../wixCodeViewerApp');
const experiments = require('../../stores/selectors/experiments');

function createLoadHandler({store, messageService, pubSubService}) {
  const santaVersion = getSantaVersionFromWorkerUrl(_.get(self, 'location.href'));

  function createRemoteGlobalsInterface(context) {
    const RGI = new platformUtils.RemoteGlobalsInterface(context);
    return RGI;
  }

  function initGlobals(RGI, sdkParameters, minimalRoutersMap, sdk) {
    sdk.__INTERNAL__.initGlobals({ RGI });
    sdk.__INTERNAL__.addEnvParams(_.assign({}, sdkParameters, { routersMap: minimalRoutersMap }));
  }


  function startApplications(apps, routersMap, storageValue, workerId, getApp, sdk) {
    return Promise.all(apps.map(appDef => {
      const app = getApp(appDef.id);
      app.module.initAppForPage = app.module.initAppForPage || _.noop;
      const initAppParam = {
        appInstanceId: appDef.id,
        instanceId: appDef.instanceId,
        appDefinitionId: appDef.id
      };
      if (appDef.routerData) {
        initAppParam.routerReturnedData = appDef.routerData;
      }
      if (appDef.instance) {
        initAppParam.instance = appDef.instance;
      }
      if (appDef.url) {
        initAppParam.url = appDef.url;
      }
      if (appDef.baseUrls) {
        initAppParam.baseUrls = appDef.baseUrls;
      }

      initAppParam.appRouters = _.filter(routersMap, routerDef => routerDef.appDefinitionId === appDef.id);
      const appStorage = sdk.getStorage(`${appDef.id}_${appDef.instanceId}`, storageValue);
      const beforeInit = Date.now();
      const scopedGlobalSdkApis = sdk.getScopedGlobalApis(app.appDefId);
      const boundedPubSubService = pubSubService.getBoundedAPI(workerId, appDef.id);
      const platformApi = platformUtilities.getApi(routersMap, appStorage, boundedPubSubService);
      const platformServicesAPI = platformServices.getApi(app.appDefId, workerId, store);

      app.appParams = initAppParam;
      app.platformAPIs = _.merge({}, platformApi, platformServicesAPI);
      app.wixCodeApi = scopedGlobalSdkApis;

      try {
        app.ready = Promise.resolve(app.module.initAppForPage(initAppParam, platformApi, scopedGlobalSdkApis, platformServicesAPI));
      } catch (err) {
        /*eslint-disable no-console*/
        console.error(err);
        app.ready = Promise.reject(new Error(err));
      }
      return app.ready
        .then(function() {
          const afterInit = Date.now();
          workerUtils.reportPlatformRenderStepBI({
            workerId,
            appId: appDef.id,
            timeSpent: afterInit - beforeInit,
            actionName: bi.ACTION_NAMES.INIT_APP_FOR_PAGE
          });
          return Promise.resolve();
        })
        .catch(function(err) {
          const errorMsg = err && err.message;
          workerUtils.reportBI(errors.INIT_APP_FOR_PAGE_FAILED, {
            app_id: appDef.id,
            worker_id: workerId,
            error: errorMsg,
            ts: biStore.getBiSession().ts
          });
          /*eslint-disable no-console*/
          console.error(errorMsg);
        });
    }));
  }

  function getSantaVersionFromWorkerUrl(workerUrl = '') {
    const santaVersionMatch = workerUrl.match(/santa\/([^/]*)/);
    return santaVersionMatch ? santaVersionMatch[1] : 'unknown';
  }

  function getSessionTags() {
    return {
      fullUrl: _.get(self['wix-location'], 'url'),
      viewMode: store.getValue(storeConstants.VIEW_MODE),
      santaVersion
    };
  }

  function getSessionParameters() {
    return {
      renderingEnvironment: store.getValue(storeConstants.RENDERING_ENV),
      referrer: _.get(self['wix-window'], 'referrer')
    };
  }

  function getUrl() {
    return _.get(self['wix-location'], 'url') || _.get(self, 'location.href');
  }

  function sessionDataCallback() {
    return {
      extra: getSessionParameters(),
      tags: getSessionTags(),
      request: { url: getUrl() }
    };
  }

  function getWixcodeBiServices(userCodeContextIds) {
    const viewMode = store.getValue(storeConstants.VIEW_MODE);
    return _.assign(
      bi.getBiServices(messageService.sendMessage),
      { userCodeContextIds, viewMode }
    );
  }


  function handleLoad({
    applications,
    routersMap,
    wixCode,
    elementoryArguments,
    sdkParameters,
    storage,
    rgi,
    doNotLoadUserCode
  }, { workerId, getApp, userCodeStore, sdk }) {
    if (!Array.isArray(applications)) {
      throw new Error('Load message data must include applications property of type Array');
    }

    const useWixCodeApp = experiments.isExperimentOpen(store, USE_WIX_CODE_APP_EXPERIMENT);
    if (!routersMap) {
      throw new Error('Load message data must include routersMap of type object');
    }

    scriptsValidator.validate(wixCode, ['id', 'url', 'scriptName', 'displayName']);

    const routerData = _.get(wixCode, ['0', 'routerData']);

    if (!useWixCodeApp) {
      if (routerData) {
        self.routerData = routerData;
      }
      if (elementoryArguments) {
        // in case of wix code viewer app, startUserCode sets ElementoryArguments on self
        setElementoryArguments(elementoryArguments);
      }
    }

    const minimalRoutersMap = _.mapValues(routersMap, value => ({ prefix: value.prefix, pages: value.pages }));

    const RGI = createRemoteGlobalsInterface(rgi);
    initGlobals(RGI, sdkParameters, minimalRoutersMap, sdk);

    biStore.getBiSession().pageId = _.get(rgi, 'navigation.currentPageId', '');
    biStore.getBiSession().pageUrl = _.get(rgi, 'navigation.currentPageFullUrl', '');
    const renderingEnv = store.getValue(storeConstants.RENDERING_ENV);
    const queryParamKeys = _.keys(_.get(self['wix-location'], 'query'));
    const sessionShouldSendCallback = ravenWorkerServices.createSessionShouldSendCallback(renderingEnv, queryParamKeys);
    ravenWorkerServices.setSessionShouldSendCallback(sessionShouldSendCallback);
    ravenWorkerServices.setSessionDataCallback(sessionDataCallback);

    if (wixCode && !doNotLoadUserCode) {
      let mergedUserExports;
      if (useWixCodeApp) {
        sdk.__INTERNAL__.addAppStudioGlobalsIfNeeded();
        const scriptAndSelector = wixCode.map(wixCodeScript => ({
          script: wixCodeScript,
          $w: sdk.getSelector(wixCodeScript.id, wixCodeScript.displayName),
          storage: sdk.getStorage('wixCode', storage)
        }));

        // startUserCode sets ElementoryArguments on self
        mergedUserExports = wixCodeViewerApp.startUserCode(scriptAndSelector, {elementoryArguments, routerData});
      } else {
        const modules = userCodeStore.startUserCode(wixCode, storage, sdk);
        mergedUserExports = utils.assign.apply(utils, _.compact([{}].concat(modules)));
      }
      sdk.__INTERNAL__.setStaticEventHandlers(mergedUserExports, workerId);
      sdk.__INTERNAL__.setBiServices(getWixcodeBiServices(_.map(wixCode, 'id')));
    }

    return startApplications(applications, routersMap, storage, workerId, getApp, sdk);
  }

  return handleLoad;
}

module.exports = createLoadHandler;
